import React, { useState, useEffect, useRef } from 'react';
import { X, Save, Plus, AlertCircle, FileEdit, Copy, Calendar, HelpCircle, Clock } from 'lucide-react';

import { calculateEffectiveStatus } from '../utils/statusUtils';
import { normalizeCompoundCategory } from '../utils/sortUtils';
import { parseFrequencySchedule, mapTimingToSlots, TIME_SLOTS, PRN_SLOT } from '../utils/calendarUtils';
import { 
  HEALTH_CATEGORIES, 
  getEffectivePrimaryCategory, 
  getEffectiveSecondaryCategory 
} from '../utils/healthCategories';

export default function ProtocolModal({ isOpen, onClose, onSave, protocolToEdit, isDuplicate = false }) {
  const isEdit = Boolean(protocolToEdit) && !isDuplicate;

  const [formData, setFormData] = useState({
    name: '',
    category: 'Supplement',
    primaryCategory: 'General or Other Health',
    secondaryCategory: '',
    dosageMin: '',
    dosageMax: '',
    weeklyTotalDosage: '',
    unit: 'mg',
    administrationFormat: 'Oral',
    timingOfDay: 'AM',
    frequencySchedule: 'Daily',
    brandOrSupplier: '',
    intendedOutcome: '',
    notes: '',
    cycleStatus: 'Active',
    startDate: new Date().toISOString().split('T')[0],
    endDate: ''
  });

  const [changeNotes, setChangeNotes] = useState('');
  const [error, setError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [showFrequencyInfo, setShowFrequencyInfo] = useState(false);
  const [showTimingInfo, setShowTimingInfo] = useState(false);
  const frequencyInfoRef = useRef(null);
  const timingInfoRef = useRef(null);

  const requestClose = () => {
    if (isSaving) return;
    onClose();
  };

  useEffect(() => {
    function handleClickOutside(event) {
      if (frequencyInfoRef.current && !frequencyInfoRef.current.contains(event.target)) {
        setShowFrequencyInfo(false);
      }
      if (timingInfoRef.current && !timingInfoRef.current.contains(event.target)) {
        setShowTimingInfo(false);
      }
    }
    if (showFrequencyInfo || showTimingInfo) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showFrequencyInfo, showTimingInfo]);

  useEffect(() => {
    function handleEscape(event) {
      if (event.key === 'Escape') {
        if (isSaving) return;
        onClose();
      }
    }
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, isSaving, onClose]);

  useEffect(() => {
    if (protocolToEdit) {
      setFormData({
        name: protocolToEdit.name || '',
        category: normalizeCompoundCategory(protocolToEdit.category) || 'Supplement',
        primaryCategory: protocolToEdit.primaryCategory || getEffectivePrimaryCategory(protocolToEdit),
        secondaryCategory: protocolToEdit.secondaryCategory !== undefined ? (protocolToEdit.secondaryCategory || '') : (getEffectiveSecondaryCategory(protocolToEdit) || ''),
        dosageMin: protocolToEdit.dosageMin !== undefined ? protocolToEdit.dosageMin : '',
        dosageMax: protocolToEdit.dosageMax !== undefined && protocolToEdit.dosageMax !== null ? protocolToEdit.dosageMax : '',
        weeklyTotalDosage: protocolToEdit.weeklyTotalDosage !== undefined && protocolToEdit.weeklyTotalDosage !== null ? protocolToEdit.weeklyTotalDosage : '',
        unit: protocolToEdit.unit || 'mg',
        administrationFormat: protocolToEdit.administrationFormat || 'Oral',
        timingOfDay: protocolToEdit.timingOfDay || 'AM',
        frequencySchedule: protocolToEdit.frequencySchedule || 'Daily',
        brandOrSupplier: protocolToEdit.brandOrSupplier || '',
        intendedOutcome: protocolToEdit.intendedOutcome || '',
        notes: protocolToEdit.notes || '',
        cycleStatus: protocolToEdit.cycleStatus || 'Active',
        startDate: protocolToEdit.startDate || new Date().toISOString().split('T')[0],
        endDate: protocolToEdit.endDate || ''
      });
      setChangeNotes('');
    } else {
      setFormData({
        name: '',
        category: 'Supplement',
        primaryCategory: 'General or Other Health',
        secondaryCategory: '',
        dosageMin: '',
        dosageMax: '',
        weeklyTotalDosage: '',
        unit: 'mg',
        administrationFormat: 'Oral',
        timingOfDay: 'AM',
        frequencySchedule: 'Daily',
        brandOrSupplier: '',
        intendedOutcome: '',
        notes: '',
        cycleStatus: 'Active',
        startDate: new Date().toISOString().split('T')[0],
        endDate: ''
      });
      setChangeNotes('');
    }
    setError('');
    setIsSaving(false);
  }, [protocolToEdit, isDuplicate, isOpen]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const updated = { ...prev, [name]: value };
      const today = new Date().toISOString().split('T')[0];
      
      // Auto-update status when start date changes (future start date => Pending)
      if (name === 'startDate' && value) {
        if (value > today) {
          updated.cycleStatus = 'Pending';
        } else if (updated.cycleStatus === 'Pending' && value <= today) {
          updated.cycleStatus = 'Active';
        }
      }

      // Auto-update status when end date changes (past end date => Completed)
      if (name === 'endDate' && value) {
        if (today > value) {
          updated.cycleStatus = 'Completed';
        }
      }

      // If user sets Active but start date is in future, align start date to today
      if (name === 'cycleStatus' && value === 'Active' && updated.startDate > today) {
        updated.startDate = today;
      }

      return updated;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSaving) return;
    setError('');

    if (!formData.name.trim()) {
      setError('Compound name is required.');
      return;
    }

    if (formData.dosageMin === '' || isNaN(Number(formData.dosageMin))) {
      setError('A valid minimum dosage is required.');
      return;
    }

    const today = new Date().toISOString().split('T')[0];
    const finalStatus = calculateEffectiveStatus(formData, today);

    const payload = {
      ...formData,
      primaryCategory: formData.primaryCategory || 'General or Other Health',
      secondaryCategory: formData.secondaryCategory && formData.secondaryCategory !== formData.primaryCategory ? formData.secondaryCategory : null,
      cycleStatus: finalStatus,
      dosageMin: Number(formData.dosageMin),
      dosageMax: formData.dosageMax !== '' && formData.dosageMax !== null ? Number(formData.dosageMax) : null,
      weeklyTotalDosage: formData.weeklyTotalDosage !== '' && formData.weeklyTotalDosage !== null ? Number(formData.weeklyTotalDosage) : null,
      endDate: formData.endDate ? formData.endDate : null
    };

    setIsSaving(true);
    try {
      await onSave(payload, changeNotes);
      onClose();
    } catch (err) {
      setError(err?.message || 'Failed to save protocol. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 bg-slate-950/80 backdrop-blur-sm overflow-y-auto animate-fadeIn touch-pan-y">
      <div className="bg-slate-900 border border-slate-800 rounded-2xl max-w-2xl w-full flex flex-col max-h-[92vh] sm:max-h-[88vh] shadow-2xl relative my-auto overflow-hidden">
        
        {/* Sticky Header */}
        <div className="flex items-center justify-between p-4 sm:p-5 border-b border-slate-800 shrink-0 bg-slate-900">
          <div className="flex items-center space-x-3">
            <div className={`p-2.5 rounded-xl border ${
              isDuplicate
                ? 'bg-indigo-500/10 text-indigo-400 border-indigo-500/20'
                : isEdit
                ? 'bg-sky-500/10 text-sky-400 border-sky-500/20'
                : 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
            }`}>
              {isDuplicate ? (
                <Copy className="w-5 h-5" />
              ) : isEdit ? (
                <FileEdit className="w-5 h-5" />
              ) : (
                <Plus className="w-5 h-5" />
              )}
            </div>
            <div>
              <h2 className="text-base sm:text-lg font-bold text-white">
                {isDuplicate 
                  ? `Duplicate ${protocolToEdit?.name || 'Protocol'}` 
                  : isEdit 
                  ? `Edit ${protocolToEdit?.name}` 
                  : 'Add New Protocol Item'}
              </h2>
              <p className="text-[11px] sm:text-xs text-slate-400">
                {isDuplicate 
                  ? 'Review and modify values to create a new duplicated protocol' 
                  : isEdit 
                  ? 'Update dosage, schedules, or change protocol status' 
                  : 'Enter compound details for stack tracking'}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={requestClose}
            disabled={isSaving}
            className="text-slate-400 hover:text-white p-2 rounded-lg hover:bg-slate-800 transition disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Scrollable Form Body */}
        <div className="p-4 sm:p-6 overflow-y-auto flex-1 touch-pan-y space-y-4 text-xs">
          {error && (
            <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-xs flex items-center space-x-2">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <form id="protocol-form" onSubmit={handleSubmit} className="space-y-4">
            
            {/* Row 1: Name & Compound Classification */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="sm:col-span-2">
                <label className="block text-slate-300 font-medium mb-1">Compound / Protocol Name *</label>
                <input
                  type="text"
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="e.g. Tirzepatide, NMN, or Testosterone"
                  className="w-full px-3 py-2.5 sm:py-2 bg-slate-950 border border-slate-800 rounded-xl text-white placeholder-slate-600 focus:outline-none focus:border-sky-500 transition"
                />
              </div>

              <div>
                <label className="block text-slate-300 font-medium mb-1" title="Biochemical compound classification">
                  Compound Type *
                </label>
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  className="w-full px-3 py-2.5 sm:py-2 bg-slate-950 border border-slate-800 rounded-xl text-white focus:outline-none focus:border-sky-500 transition cursor-pointer"
                >
                  <option value="Supplement">Supplement</option>
                  <option value="Peptide">Peptide</option>
                  <option value="Hormones & AAS">Hormones & AAS</option>
                </select>
              </div>
            </div>

            {/* Row 1b: Primary & Secondary Health Categories */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-3 rounded-xl bg-slate-950/60 border border-slate-800/80">
              <div>
                <label className="block text-slate-300 font-medium mb-1 flex items-center justify-between">
                  <span>Primary Category *</span>
                  <span className="text-[10px] text-sky-400 font-normal">Primary Goal / Stack Grouping</span>
                </label>
                <select
                  name="primaryCategory"
                  value={formData.primaryCategory}
                  onChange={handleChange}
                  className="w-full px-3 py-2.5 sm:py-2 bg-slate-900 border border-slate-800 rounded-xl text-white focus:outline-none focus:border-sky-500 transition cursor-pointer font-medium"
                >
                  {HEALTH_CATEGORIES.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-slate-300 font-medium mb-1 flex items-center justify-between">
                  <span>Secondary Category</span>
                  <span className="text-[10px] text-slate-500 font-normal">Optional Target</span>
                </label>
                <select
                  name="secondaryCategory"
                  value={formData.secondaryCategory || ''}
                  onChange={handleChange}
                  className="w-full px-3 py-2.5 sm:py-2 bg-slate-900 border border-slate-800 rounded-xl text-white focus:outline-none focus:border-sky-500 transition cursor-pointer"
                >
                  <option value="">-- None / Optional --</option>
                  {HEALTH_CATEGORIES.filter(cat => cat !== formData.primaryCategory).map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Row 2: Dosage Range, Weekly Total & Unit */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 items-end">
              <div className="flex flex-col justify-end">
                <label className="block text-slate-300 font-medium mb-1 text-[11px] sm:text-xs whitespace-nowrap truncate" title="Min / Discrete Dose *">
                  Min / Discrete Dose *
                </label>
                <input
                  type="number"
                  step="any"
                  name="dosageMin"
                  required
                  value={formData.dosageMin}
                  onChange={handleChange}
                  placeholder="e.g. 500 or 2.5"
                  className="w-full px-3 py-2.5 sm:py-2 bg-slate-950 border border-slate-800 rounded-xl text-white placeholder-slate-600 focus:outline-none focus:border-sky-500 transition"
                />
              </div>

              <div className="flex flex-col justify-end">
                <label className="block text-slate-300 font-medium mb-1 text-[11px] sm:text-xs whitespace-nowrap truncate" title="Max Dose (Optional)">
                  Max Dose (Optional)
                </label>
                <input
                  type="number"
                  step="any"
                  name="dosageMax"
                  value={formData.dosageMax}
                  onChange={handleChange}
                  placeholder="e.g. 1000 (if range)"
                  className="w-full px-3 py-2.5 sm:py-2 bg-slate-950 border border-slate-800 rounded-xl text-white placeholder-slate-600 focus:outline-none focus:border-sky-500 transition"
                />
              </div>

              <div className="flex flex-col justify-end">
                <label className="block text-slate-300 font-medium mb-1 text-[11px] sm:text-xs whitespace-nowrap truncate" title="Weekly Total Dosage (Optional)">
                  Weekly Total (Optional)
                </label>
                <input
                  type="number"
                  step="any"
                  name="weeklyTotalDosage"
                  value={formData.weeklyTotalDosage}
                  onChange={handleChange}
                  placeholder="e.g. 100 or 175"
                  className="w-full px-3 py-2.5 sm:py-2 bg-slate-950 border border-slate-800 rounded-xl text-white placeholder-slate-600 focus:outline-none focus:border-sky-500 transition"
                />
              </div>

              <div className="flex flex-col justify-end">
                <label className="block text-slate-300 font-medium mb-1 text-[11px] sm:text-xs whitespace-nowrap truncate" title="Dosage Unit *">
                  Dosage Unit *
                </label>
                <input
                  type="text"
                  name="unit"
                  required
                  value={formData.unit}
                  onChange={handleChange}
                  placeholder="e.g. mg, mcg, IU"
                  className="w-full px-3 py-2.5 sm:py-2 bg-slate-950 border border-slate-800 rounded-xl text-white placeholder-slate-600 focus:outline-none focus:border-sky-500 transition"
                />
              </div>
            </div>

            {/* Row 3: Admin Format, Timing, Frequency */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-slate-300 font-medium mb-1">Admin Format</label>
                <input
                  type="text"
                  name="administrationFormat"
                  value={formData.administrationFormat}
                  onChange={handleChange}
                  placeholder="Oral, Subcutaneous Injection..."
                  className="w-full px-3 py-2.5 sm:py-2 bg-slate-950 border border-slate-800 rounded-xl text-white placeholder-slate-600 focus:outline-none focus:border-sky-500 transition"
                />
              </div>

              <div className="relative">
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center space-x-1.5">
                    <label className="block text-slate-300 font-medium">Timing of Day</label>
                    <button
                      type="button"
                      onClick={() => {
                        setShowTimingInfo(prev => !prev);
                        setShowFrequencyInfo(false);
                      }}
                      className="text-slate-400 hover:text-sky-400 p-0.5 rounded-full hover:bg-slate-800/80 transition flex items-center justify-center focus:outline-none"
                      title="View recommended timing values and calendar mapping"
                      aria-label="View recommended timing values and calendar mapping"
                    >
                      <HelpCircle className="w-3.5 h-3.5 text-sky-400/80 hover:text-sky-300" />
                    </button>
                  </div>
                </div>

                {/* Info Popover Overlay */}
                {showTimingInfo && (
                  <div
                    ref={timingInfoRef}
                    className="absolute left-0 right-0 sm:left-1/2 sm:-translate-x-1/2 top-7 z-50 w-full sm:w-96 p-3.5 bg-slate-900/95 backdrop-blur-md border border-slate-700/90 rounded-2xl shadow-2xl shadow-black/80 text-xs space-y-3 animate-in fade-in zoom-in-95 duration-150"
                  >
                    <div className="flex items-start justify-between border-b border-slate-800 pb-2">
                      <div>
                        <h4 className="font-semibold text-slate-200 flex items-center gap-1.5">
                          <Clock className="w-3.5 h-3.5 text-amber-400" />
                          Recommended Timing & Calendar Slots
                        </h4>
                        <p className="text-[11px] text-slate-400 mt-0.5">
                          Values correlate to calendar time slots. Click to select:
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setShowTimingInfo(false)}
                        className="text-slate-400 hover:text-white p-1 hover:bg-slate-800 rounded-lg transition"
                        aria-label="Close timing tips"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>

                    <div className="space-y-2.5 max-h-64 overflow-y-auto pr-1">
                      {/* Morning / AM */}
                      <div>
                        <div className="text-[10px] font-semibold uppercase tracking-wider text-amber-400/90 mb-1 flex items-center justify-between">
                          <span>Morning / AM Slot</span>
                          <span className="text-slate-400 font-normal">Wake-up – 11:00 AM</span>
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {['AM', 'Morning Fasted', 'Upon Waking', 'With Breakfast'].map((item) => (
                            <button
                              key={item}
                              type="button"
                              onClick={() => {
                                setFormData(prev => ({ ...prev, timingOfDay: item }));
                                setShowTimingInfo(false);
                              }}
                              className="px-2 py-0.5 rounded-lg bg-slate-800/90 hover:bg-amber-500/20 text-slate-300 hover:text-amber-300 border border-slate-700/80 hover:border-amber-500/40 font-mono text-[11px] transition text-left"
                            >
                              {item}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Midday / Noon */}
                      <div>
                        <div className="text-[10px] font-semibold uppercase tracking-wider text-sky-400/90 mb-1 flex items-center justify-between">
                          <span>Midday / Noon Slot</span>
                          <span className="text-slate-400 font-normal">11:00 AM – 4:00 PM</span>
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {['Noon', 'With Lunch', 'Afternoon'].map((item) => (
                            <button
                              key={item}
                              type="button"
                              onClick={() => {
                                setFormData(prev => ({ ...prev, timingOfDay: item }));
                                setShowTimingInfo(false);
                              }}
                              className="px-2 py-0.5 rounded-lg bg-slate-800/90 hover:bg-sky-500/20 text-slate-300 hover:text-sky-300 border border-slate-700/80 hover:border-sky-500/40 font-mono text-[11px] transition text-left"
                            >
                              {item}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Evening / Bedtime */}
                      <div>
                        <div className="text-[10px] font-semibold uppercase tracking-wider text-indigo-400/90 mb-1 flex items-center justify-between">
                          <span>Evening / Bedtime Slot</span>
                          <span className="text-slate-400 font-normal">4:00 PM – Sleep</span>
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {['Bedtime', 'Evening', 'Before Sleep (Fasted)', 'With Dinner'].map((item) => (
                            <button
                              key={item}
                              type="button"
                              onClick={() => {
                                setFormData(prev => ({ ...prev, timingOfDay: item }));
                                setShowTimingInfo(false);
                              }}
                              className="px-2 py-0.5 rounded-lg bg-slate-800/90 hover:bg-indigo-500/20 text-slate-300 hover:text-indigo-300 border border-slate-700/80 hover:border-indigo-500/40 font-mono text-[11px] transition text-left"
                            >
                              {item}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Split Dosing / Multi-Slot */}
                      <div>
                        <div className="text-[10px] font-semibold uppercase tracking-wider text-purple-400/90 mb-1 flex items-center justify-between">
                          <span>Multi-Slot / Split Dosing</span>
                          <span className="text-slate-400 font-normal">Plots in 2+ Slots</span>
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {['Twice Daily (AM & Bedtime)', 'AM / Evening', 'Twice Daily (AM & Bedtime Fasted)', 'Three Times Daily (TID)'].map((item) => (
                            <button
                              key={item}
                              type="button"
                              onClick={() => {
                                setFormData(prev => ({ ...prev, timingOfDay: item }));
                                setShowTimingInfo(false);
                              }}
                              className="px-2 py-0.5 rounded-lg bg-slate-800/90 hover:bg-purple-500/20 text-slate-300 hover:text-purple-300 border border-slate-700/80 hover:border-purple-500/40 font-mono text-[11px] transition text-left"
                            >
                              {item}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Flexible / As Needed */}
                      <div>
                        <div className="text-[10px] font-semibold uppercase tracking-wider text-emerald-400/90 mb-1 flex items-center justify-between">
                          <span>Flexible / As Needed</span>
                          <span className="text-slate-400 font-normal">PRN Slot</span>
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {['PRN / As Needed', 'Pre-Workout', 'Post-Workout'].map((item) => (
                            <button
                              key={item}
                              type="button"
                              onClick={() => {
                                setFormData(prev => ({ ...prev, timingOfDay: item }));
                                setShowTimingInfo(false);
                              }}
                              className="px-2 py-0.5 rounded-lg bg-slate-800/90 hover:bg-emerald-500/20 text-slate-300 hover:text-emerald-300 border border-slate-700/80 hover:border-emerald-500/40 font-mono text-[11px] transition text-left"
                            >
                              {item}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <input
                  type="text"
                  name="timingOfDay"
                  value={formData.timingOfDay}
                  onChange={handleChange}
                  placeholder="AM, Bedtime, Twice Daily..."
                  className="w-full px-3 py-2.5 sm:py-2 bg-slate-950 border border-slate-800 rounded-xl text-white placeholder-slate-600 focus:outline-none focus:border-sky-500 transition"
                />
                {formData.timingOfDay && (
                  <div className="mt-1.5 flex items-center gap-1.5 text-xs flex-wrap">
                    <span className="text-slate-400">Calendar Slot:</span>
                    {mapTimingToSlots(formData.timingOfDay).map(slotId => {
                      const slotDef = TIME_SLOTS.find(s => s.id === slotId) || (slotId === 'PRN' ? PRN_SLOT : null);
                      const label = slotDef ? slotDef.shortLabel || slotDef.label : slotId;
                      const isAm = slotId === 'AM';
                      const isNoon = slotId === 'NOON';
                      const isBedtime = slotId === 'BEDTIME';
                      return (
                        <span
                          key={slotId}
                          className={`px-2 py-0.5 rounded-md font-medium inline-flex items-center gap-1 border ${
                            isAm
                              ? 'bg-amber-500/10 border-amber-500/30 text-amber-300'
                              : isNoon
                              ? 'bg-sky-500/10 border-sky-500/30 text-sky-300'
                              : isBedtime
                              ? 'bg-indigo-500/10 border-indigo-500/30 text-indigo-300'
                              : 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300'
                          }`}
                        >
                          <Clock className="w-3 h-3" />
                          {label}
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="relative">
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center space-x-1.5">
                    <label className="block text-slate-300 font-medium">Frequency Schedule</label>
                    <button
                      type="button"
                      onClick={() => {
                        setShowFrequencyInfo(prev => !prev);
                        setShowTimingInfo(false);
                      }}
                      className="text-slate-400 hover:text-sky-400 p-0.5 rounded-full hover:bg-slate-800/80 transition flex items-center justify-center focus:outline-none"
                      title="View frequency schedule examples and shortcuts"
                      aria-label="View frequency schedule examples and shortcuts"
                    >
                      <HelpCircle className="w-3.5 h-3.5 text-sky-400/80 hover:text-sky-300" />
                    </button>
                  </div>
                </div>

                {/* Info Popover Overlay */}
                {showFrequencyInfo && (
                  <div
                    ref={frequencyInfoRef}
                    className="absolute left-0 right-0 sm:left-auto sm:right-0 top-7 z-50 w-full sm:w-96 p-3.5 bg-slate-900/95 backdrop-blur-md border border-slate-700/90 rounded-2xl shadow-2xl shadow-black/80 text-xs space-y-3 animate-in fade-in zoom-in-95 duration-150"
                  >
                    <div className="flex items-start justify-between border-b border-slate-800 pb-2">
                      <div>
                        <h4 className="font-semibold text-slate-200 flex items-center gap-1.5">
                          <Calendar className="w-3.5 h-3.5 text-sky-400" />
                          Supported Frequency Formats
                        </h4>
                        <p className="text-[11px] text-slate-400 mt-0.5">
                          Type naturally or click an example below to insert:
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setShowFrequencyInfo(false)}
                        className="text-slate-400 hover:text-white p-1 hover:bg-slate-800 rounded-lg transition"
                        aria-label="Close frequency tips"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>

                    <div className="space-y-2.5 max-h-64 overflow-y-auto pr-1">
                      {/* Day Intervals */}
                      <div>
                        <div className="text-[10px] font-semibold uppercase tracking-wider text-sky-400/90 mb-1">
                          Day Intervals & Numbers
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {['every 3 days', 'every four days', '3 days apart', 'every 3rd', 'every 4', '3'].map((item) => (
                            <button
                              key={item}
                              type="button"
                              onClick={() => {
                                setFormData(prev => ({ ...prev, frequencySchedule: item }));
                                setShowFrequencyInfo(false);
                              }}
                              className="px-2 py-0.5 rounded-lg bg-slate-800/90 hover:bg-sky-500/20 text-slate-300 hover:text-sky-300 border border-slate-700/80 hover:border-sky-500/40 font-mono text-[11px] transition text-left"
                            >
                              {item}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Specific Days */}
                      <div>
                        <div className="text-[10px] font-semibold uppercase tracking-wider text-emerald-400/90 mb-1">
                          Days of the Week
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {['Mon Wed Fri', 'Twice Weekly (Mon / Thu)', 'Weekly (Tuesday AM)'].map((item) => (
                            <button
                              key={item}
                              type="button"
                              onClick={() => {
                                setFormData(prev => ({ ...prev, frequencySchedule: item }));
                                setShowFrequencyInfo(false);
                              }}
                              className="px-2 py-0.5 rounded-lg bg-slate-800/90 hover:bg-emerald-500/20 text-slate-300 hover:text-emerald-300 border border-slate-700/80 hover:border-emerald-500/40 font-mono text-[11px] transition text-left"
                            >
                              {item}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Shorthand & Cycles */}
                      <div>
                        <div className="text-[10px] font-semibold uppercase tracking-wider text-indigo-400/90 mb-1">
                          Cycles & Shorthand
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {['5 days on, 2 days off', 'E3D', 'E4D', 'EOD', 'every 2 weeks'].map((item) => (
                            <button
                              key={item}
                              type="button"
                              onClick={() => {
                                setFormData(prev => ({ ...prev, frequencySchedule: item }));
                                setShowFrequencyInfo(false);
                              }}
                              className="px-2 py-0.5 rounded-lg bg-slate-800/90 hover:bg-indigo-500/20 text-slate-300 hover:text-indigo-300 border border-slate-700/80 hover:border-indigo-500/40 font-mono text-[11px] transition text-left"
                            >
                              {item}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Standard Presets */}
                      <div>
                        <div className="text-[10px] font-semibold uppercase tracking-wider text-amber-400/90 mb-1">
                          Standard Presets
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {['Daily', 'Weekdays (Mon-Fri)', 'Weekends (Sat-Sun)', 'PRN / As Needed'].map((item) => (
                            <button
                              key={item}
                              type="button"
                              onClick={() => {
                                setFormData(prev => ({ ...prev, frequencySchedule: item }));
                                setShowFrequencyInfo(false);
                              }}
                              className="px-2 py-0.5 rounded-lg bg-slate-800/90 hover:bg-amber-500/20 text-slate-300 hover:text-amber-300 border border-slate-700/80 hover:border-amber-500/40 font-mono text-[11px] transition text-left"
                            >
                              {item}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <input
                  type="text"
                  name="frequencySchedule"
                  value={formData.frequencySchedule}
                  onChange={handleChange}
                  placeholder="Daily, every 3 days, 3 days apart, e3d, 5 on 2 off..."
                  className="w-full px-3 py-2.5 sm:py-2 bg-slate-950 border border-slate-800 rounded-xl text-white placeholder-slate-600 focus:outline-none focus:border-sky-500 transition"
                />
                {formData.frequencySchedule && (
                  <div className="mt-1.5 flex items-center gap-1.5 text-xs">
                    <span className="text-slate-400">Calendar Plot:</span>
                    <span className="px-2 py-0.5 rounded-md bg-sky-500/10 border border-sky-500/30 text-sky-300 font-medium inline-flex items-center gap-1">
                      <Calendar className="w-3 h-3 text-sky-400" />
                      {parseFrequencySchedule(formData.frequencySchedule).description}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Row 4: Status, Start Date, End Date */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-slate-300 font-medium mb-1">Cycle Status</label>
                <select
                  name="cycleStatus"
                  value={formData.cycleStatus}
                  onChange={handleChange}
                  className="w-full px-3 py-2.5 sm:py-2 bg-slate-950 border border-slate-800 rounded-xl text-white focus:outline-none focus:border-sky-500 transition cursor-pointer"
                >
                  <option value="Active">Active</option>
                  <option value="Pending">Pending</option>
                  <option value="Paused">Paused</option>
                  <option value="Completed">Completed</option>
                </select>
              </div>

              <div>
                <label className="block text-slate-300 font-medium mb-1">Start Date</label>
                <input
                  type="date"
                  name="startDate"
                  value={formData.startDate}
                  onChange={handleChange}
                  className="w-full px-3 py-2.5 sm:py-2 bg-slate-950 border border-slate-800 rounded-xl text-white focus:outline-none focus:border-sky-500 transition cursor-pointer"
                />
              </div>

              <div>
                <label className="block text-slate-300 font-medium mb-1">End Date (Optional)</label>
                <input
                  type="date"
                  name="endDate"
                  value={formData.endDate || ''}
                  onChange={handleChange}
                  className="w-full px-3 py-2.5 sm:py-2 bg-slate-950 border border-slate-800 rounded-xl text-white focus:outline-none focus:border-sky-500 transition cursor-pointer"
                />
              </div>
            </div>

            {/* Row 5: Supplier & Intended Outcome */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-slate-300 font-medium mb-1">Brand / Supplier</label>
                <input
                  type="text"
                  name="brandOrSupplier"
                  value={formData.brandOrSupplier}
                  onChange={handleChange}
                  placeholder="e.g. Isprelon, Generic..."
                  className="w-full px-3 py-2.5 sm:py-2 bg-slate-950 border border-slate-800 rounded-xl text-white placeholder-slate-600 focus:outline-none focus:border-sky-500 transition"
                />
              </div>

              <div>
                <label className="block text-slate-300 font-medium mb-1">Intended Outcome / Benefits</label>
                <input
                  type="text"
                  name="intendedOutcome"
                  value={formData.intendedOutcome}
                  onChange={handleChange}
                  placeholder="e.g. Vasodilation, Recovery..."
                  className="w-full px-3 py-2.5 sm:py-2 bg-slate-950 border border-slate-800 rounded-xl text-white placeholder-slate-600 focus:outline-none focus:border-sky-500 transition"
                />
              </div>
            </div>

            {/* Row 6: Special Notes */}
            <div>
              <label className="block text-slate-300 font-medium mb-1">Notes & Instructions</label>
              <textarea
                name="notes"
                rows={2}
                value={formData.notes}
                onChange={handleChange}
                placeholder="Special instructions, fasting requirements, titration schedules..."
                className="w-full px-3 py-2.5 sm:py-2 bg-slate-950 border border-slate-800 rounded-xl text-white placeholder-slate-600 focus:outline-none focus:border-sky-500 transition"
              />
            </div>

            {/* Edit Reason field for Audit Trail */}
            {isEdit && (
              <div className="pt-2 border-t border-slate-800/80">
                <label className="block text-sky-400 font-semibold mb-1">
                  Reason for Change / Correction Note (for Audit Log)
                </label>
                <input
                  type="text"
                  value={changeNotes}
                  onChange={(e) => setChangeNotes(e.target.value)}
                  placeholder="e.g. Titrated dose up per bloodwork recommendation"
                  className="w-full px-3 py-2.5 sm:py-2 bg-slate-950 border border-sky-500/30 rounded-xl text-white placeholder-slate-600 focus:outline-none focus:border-sky-500 transition"
                />
              </div>
            )}

          </form>
        </div>

        {/* Sticky Footer */}
        <div className="p-4 sm:p-5 border-t border-slate-800 flex items-center justify-end space-x-3 shrink-0 bg-slate-900/95 backdrop-blur-sm">
          <button
            type="button"
            onClick={requestClose}
            disabled={isSaving}
            className="px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 font-medium transition text-xs disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="protocol-form"
            disabled={isSaving}
            className="px-5 py-2 rounded-xl bg-gradient-to-r from-sky-500 to-indigo-600 hover:from-sky-400 hover:to-indigo-500 text-white font-bold transition flex items-center space-x-2 shadow-lg shadow-sky-500/20 text-xs disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {isDuplicate ? <Copy className="w-4 h-4" /> : <Save className="w-4 h-4" />}
            <span>{isSaving ? 'Saving...' : isDuplicate ? 'Duplicate Protocol' : isEdit ? 'Save Changes' : 'Create Protocol'}</span>
          </button>
        </div>

      </div>
    </div>
  );
}
